/**
 * Pure helpers for the page tree (flat list with parent_page_id).
 * Used by recursive deletion, sidebar drag & drop (cycle prevention),
 * and the tests.
 */

export interface PageTreeRef {
  id: string;
  parent_page_id: string | null;
}

/**
 * IDs of the subtree rooted at `rootId` (including the root), in BFS.
 */
export function collectDescendantPageIds(pages: readonly PageTreeRef[], rootId: string): string[] {
  const childrenByParent = new Map<string, string[]>();
  for (const page of pages) {
    if (page.parent_page_id) {
      const siblings = childrenByParent.get(page.parent_page_id) ?? [];
      siblings.push(page.id);
      childrenByParent.set(page.parent_page_id, siblings);
    }
  }

  const result: string[] = [];
  const queue = [rootId];
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      break;
    }
    result.push(current);
    queue.push(...(childrenByParent.get(current) ?? []));
  }
  return result;
}

/**
 * Is `candidateId` within `ancestorId`'s subtree?
 * (includes the case candidate === ancestor — moving within itself)
 */
export function isPageInSubtree(
  pages: readonly PageTreeRef[],
  ancestorId: string,
  candidateId: string,
): boolean {
  return collectDescendantPageIds(pages, ancestorId).includes(candidateId);
}
